import { Suspense, lazy, useMemo, useState } from "react";

import { useIsDark } from "../../../lib/theme";
import type { DashboardLayout } from "../../../lib/dashboard-layout";
import { Popover } from "../../ui/Popover";
import { cn } from "../../ui/cn";
import { toRangeValue, type RangeValue } from "../RangeFilter";
import { CardFilters } from "./CardFilters";
import { ColorPanel } from "./ColorPanel";
import { resolveChart, type ChartType } from "./chart-types";
import { colorResolver, PALETTES, type PaletteId } from "./palette";
import type { ChartRow, ChartSeries, ColorBy, TableValue } from "./ChartFrame";

/**
 * The frame every report on the dashboard wears.
 *
 * One component rather than a shared stylesheet, because the chrome is where
 * the consistency the PRD asks for actually lives: the same date presets, the
 * same chart switcher, the same colour controls, the same collapse behaviour,
 * on every card without exception. A card written by hand would have all of
 * those and one of them would be subtly different.
 *
 * The card's own contribution is a render prop. It receives the resolved chart
 * type and a colour lookup, and returns whatever it wants — most return a
 * `<ChartFrame>`, but a card that is a single number or a bespoke layout can
 * still sit in the same frame and get the same header.
 *
 * State is not held here. It goes through `layout`, which is the per-user
 * arrangement on the server, so a card reopens the way it was left.
 *
 * ## Where the words went
 *
 * `subtitle` and `footnote` are still a card's to supply and are no longer
 * drawn in the card. Both now live behind the "i" beside the title: the
 * caption used to sit under the heading and the caveats under the chart, so a
 * card carrying both wrapped itself in small grey text at each end, and nine
 * of those on a page is a great deal of explanation competing with the figures
 * being explained. Neither is worth deleting -- the caveats in particular are
 * the difference between a number and a misreading -- and neither is worth
 * reading more than once.
 */

/*
 * recharts is the largest thing in the bundle by a distance, and the ticket
 * list is where agents spend the day. Loaded when a chart is first drawn, not
 * when the app boots.
 */
const ChartFrame = lazy(() =>
  import("./ChartFrame").then((module) => ({ default: module.ChartFrame })),
);

export interface ReportCardRender {
  chart: ChartType;
  colorFor: (key: string) => string;
  range: RangeValue;
}

export function ReportCard({
  id,
  title,
  subtitle,
  layout,
  charts,
  defaultRange = { preset: "this-month" },
  series = [],
  hideRange = false,
  hideChart = false,
  offerAgents = false,
  actions,
  footnote,
  children,
}: {
  /** Stable across releases: it is the key this card's setup is stored under. */
  id: string;
  title: string;
  /** The like-for-like caption — "vs. same point last month". */
  subtitle?: string;
  layout: DashboardLayout;
  /** The visualisations this card's data can honestly express. */
  charts: readonly ChartType[];
  defaultRange?: RangeValue;
  /**
   * What the colour panel offers, in the card's own order.
   *
   * Given from outside because only the card knows whether its series are two
   * fixed metrics or one row per pipeline, and the latter is not known until
   * the response arrives.
   */
  series?: readonly { key: string; label: string; color?: string | null }[];
  /** For a card whose figure is about now, where a date filter means nothing. */
  hideRange?: boolean;
  /**
   * For a card that is a figure rather than a drawing.
   *
   * Hides the chart switcher and the colour picker together, because both are
   * controls over how something is *drawn* and a card that draws nothing has
   * no answer for either. Same reasoning as `hideRange`, and the same rule the
   * dashboard applies elsewhere: a control you can see and cannot use is worse
   * than no control. Such a card still declares `charts` -- the frame resolves
   * one and hands it to the render prop -- it simply does not offer the choice.
   */
  hideChart?: boolean;
  /**
   * Offer the Agent category in the filter dropdown.
   *
   * Managers only, mirroring the page's own agent selector. Not a permission:
   * `DashboardScopeGuard` refuses an agent id that is not the caller's own
   * whoever sends it, which is what makes this a question of what is worth
   * putting in front of somebody rather than of what they may read.
   */
  offerAgents?: boolean;
  /** Card-specific controls, left of the standard cluster. */
  actions?: React.ReactNode;
  footnote?: React.ReactNode;
  children: (render: ReportCardRender) => React.ReactNode;
}) {
  const dark = useIsDark();
  const stored = layout.card(id);
  const [colorsOpen, setColorsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  const chart = resolveChart(stored.chart, charts);
  const palette = (PALETTES.find((id) => id === stored.palette) ??
    "brand") as PaletteId;
  const overrides = stored.colors ?? {};
  const range = toRangeValue(stored.range, defaultRange);
  /* Expanded unless somebody said otherwise, so a new card is never hidden. */
  const open = stored.collapsed !== true;

  const colorFor = useMemo(
    () => colorResolver({ series, overrides, palette, dark }),
    [series, overrides, palette, dark],
  );

  const resolvedSeries = series.map((entry) => ({
    key: entry.key,
    label: entry.label,
    color: colorFor(entry.key),
  }));

  return (
    <section className="rounded-lg border border-line bg-surface">
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        {/*
         * `flex-1` so the title is what gives way on a narrow screen: the
         * controls beside it are fixed-width and cannot shrink, so without it
         * they were the ones pushed onto a second row while a truncatable
         * heading kept its full width.
         *
         * That trade is right until there is no width left to give, which on a
         * phone is immediately: three controls take the whole row and the
         * headings truncated to "Tick...", "Clos..." and "Age...", which name
         * nothing. So below `sm` the title takes a row of its own and the
         * controls wrap underneath it -- the shape the four-control cards
         * already fell into, and the one that reads properly. From `sm` up this
         * is exactly as it was.
         *
         * On the wrapper rather than the button, so the info icon travels with
         * the heading instead of being pushed to the far end of the row by a
         * growing button.
         */}
        <div className="flex min-w-0 basis-full items-center gap-1.5 sm:flex-1 sm:basis-auto">
          <button
            type="button"
            onClick={() => layout.setCard(id, { collapsed: open })}
            aria-expanded={open}
            className="flex min-w-0 items-center gap-2 text-left"
          >
            <span
              aria-hidden
              className={cn(
                "text-xs text-faint transition-transform",
                open && "rotate-90",
              )}
            >
              ▶
            </span>
            <span className="block truncate text-sm font-medium text-content">
              {title}
            </span>
          </button>

        {/*
          The card's own prose, behind one icon.

          The caption used to sit under the title and the caveats under the
          chart, so a card with both was wrapped in small grey text top and
          bottom -- on a page of nine cards that is a lot of explanation
          competing with the figures it explains. Both are worth keeping and
          neither is worth reading twice, which is exactly what an icon is
          for: present, findable, and not in the way.

          Rendered only when there is something to say, so a card with no
          caption and no caveats does not grow a control that opens onto
          nothing.
        */}
        {subtitle || footnote ? (
          <Popover
            open={aboutOpen}
            onOpenChange={setAboutOpen}
            side="bottom"
            align="left"
            overlay
            label={`About ${title}`}
            trigger={
              <span
                title={`About ${title}`}
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-full",
                  "border border-line text-[9px] font-semibold text-faint",
                  "hover:border-brand-300 hover:text-brand-600 dark:hover:text-brand-400",
                )}
              >
                i
              </span>
            }
          >
            {/*
              Lighter and smaller than the card's own text, and on a tinted
              ground so it reads as a note about the card rather than as more
              of the card. Semi-transparent over the blur the popover already
              carries.
            */}
            {/* Sized here rather than on the panel: `cn` is plain clsx, so a
                `max-w-*` passed to the popover would collide with the one it
                already sets and which of them won would be down to stylesheet
                order. Constraining the content is unambiguous. */}
            <div className="w-[19rem] max-w-full space-y-1 rounded-lg bg-surface-2/70 px-2.5 py-2 text-[10px] leading-relaxed text-muted">
              {subtitle ? <p>{subtitle}</p> : null}
              {footnote}
            </div>
          </Popover>
          ) : null}
        </div>

        {/* Controls stay reachable while collapsed on purpose: changing the
            range on a shut card and then opening it is a normal way to work. */}
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {actions}

          {/*
            One button for everything that narrows the card. The date range,
            the chart type and the two multi-selects used to be three or four
            controls in a row beside the heading, which on a phone wrapped
            before the card had drawn anything.

            A card with neither a range nor a chart choice gets nothing, as it
            did before: both of those are controls over something it cannot
            answer, and the filters alone would be a button that opens onto one
            row.
          */}
          {hideRange && hideChart ? null : (
            <CardFilters
              title={title}
              charts={hideChart ? [] : charts}
              offerAgents={offerAgents}
              value={{
                range,
                chart,
                pipelineIds: stored.pipelineIds ?? [],
                agentIds: stored.agentIds ?? [],
              }}
              onChange={(next) => layout.setCard(id, next)}
            />
          )}

          {hideChart ? null : (
          <Popover
            open={colorsOpen}
            onOpenChange={setColorsOpen}
            side="bottom"
            align="right"
            overlay
            label={`Colours for ${title}`}
            className="max-h-[32rem] overflow-y-auto"
            trigger={
              <span
                title="Colours"
                className={cn(
                  "flex size-7 items-center justify-center rounded-md border border-line",
                  "text-muted hover:border-brand-300 hover:text-brand-600 dark:hover:text-brand-400",
                )}
              >
                {/* Three stacked swatches: it reads as "colours" at 12px,
                    where a cog reads as "settings" and would be a lie. */}
                <span aria-hidden className="flex flex-col gap-px">
                  {resolvedSeries.slice(0, 3).map((entry) => (
                    <span
                      key={entry.key}
                      className="block h-[3px] w-3 rounded-full"
                      style={{ backgroundColor: entry.color }}
                    />
                  ))}
                  {resolvedSeries.length === 0 ? (
                    <span className="block size-3 rounded-full bg-surface-2" />
                  ) : null}
                </span>
              </span>
            }
          >
            <ColorPanel
              series={resolvedSeries}
              palette={palette}
              overrides={overrides}
              dark={dark}
              onPaletteChange={(next) => layout.setCard(id, { palette: next })}
              onColorChange={(key, color) => {
                const next = { ...overrides };
                if (color === null) delete next[key];
                else next[key] = color;
                layout.setCard(id, { colors: next });
              }}
            />
          </Popover>
          )}
        </div>
      </header>

      {open ? (
        <div className="p-3">{children({ chart, colorFor, range })}</div>
      ) : null}
    </section>
  );
}

/**
 * A chart inside a card, with the loading state the lazy import needs.
 *
 * Separate from `ReportCard` so a card that draws something other than a chart
 * — a single figure, a heatmap — does not pay for recharts at all.
 */
export function CardChart(props: {
  chart: ChartType;
  rows: ChartRow[];
  series: ChartSeries[];
  colorFor: (key: string) => string;
  /** "row" for categorical cards -- see `ColorBy` in ChartFrame. */
  colorBy?: ColorBy;
  format: (value: number | null) => string;
  /** Table view only -- lets a count open into the tickets behind it. */
  renderValue?: TableValue;
  height?: number;
  emptyMessage?: string;
}) {
  const { chart, ...rest } = props;

  // A table needs no chart library, so it renders without waiting for one.
  return (
    <Suspense
      fallback={
        <div
          style={{ height: props.height ?? 224 }}
          className="flex items-center justify-center text-xs text-faint"
        >
          Drawing…
        </div>
      }
    >
      <ChartFrame type={chart} {...rest} />
    </Suspense>
  );
}
